import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import { useAuth } from '../hooks/useAuth';
import { getReportPatterns } from '../services/reportService';
import { getBrands } from '../services/brandService';
import { DashboardListSkeleton } from '../components/dashboard/PageSkeletons';
import {
  PAGE_TYPES,
  CHANGE_TYPES,
  RESULT_FILTERS,
  getPageTypeLabel,
  getChangeTypeLabel,
  getOutcomeChip,
} from '../utils/reportTaxonomy';
import { BRAND } from '../theme/brandTheme';

/**
 * Filterable pattern archive — tag aggregates + report list
 */
const TestLibraryPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [brands, setBrands] = useState([]);

  const filters = useMemo(
    () => ({
      brand: searchParams.get('brand') || '',
      tag: searchParams.get('tag') || '',
      pageType: searchParams.get('pageType') || '',
      changeType: searchParams.get('changeType') || '',
      result: searchParams.get('result') || '',
    }),
    [searchParams],
  );

  const setFilter = useCallback(
    (key, value) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = () => setSearchParams({}, { replace: true });

  // Load brand list for admin/editor filter dropdown
  useEffect(() => {
    const loadBrands = async () => {
      if (!user) return;
      try {
        if (user.role === 'admin' || user.role === 'editor') {
          const list = await getBrands();
          setBrands(list || []);
        } else {
          setBrands(user.brands || []);
        }
      } catch {
        setBrands([]);
      }
    };
    loadBrands();
  }, [user]);

  // Fetch pattern aggregates whenever URL filters change
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const result = await getReportPatterns(filters);
        setData(result);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filters]);

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <LocalLibraryIcon sx={{ color: BRAND.purple, fontSize: 32 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND.purple }}>
            Test Kütüphanesi
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Etiket ve taxonomy bazlı pattern arşivi — geçmiş testlerden öğren
          </Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr) auto' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            select
            label="Marka"
            size="small"
            value={filters.brand}
            onChange={(e) => setFilter('brand', e.target.value)}
          >
            <MenuItem value="">Tümü</MenuItem>
            {brands.map((b) => (
              <MenuItem key={b._id} value={b._id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Sayfa tipi"
            size="small"
            value={filters.pageType}
            onChange={(e) => setFilter('pageType', e.target.value)}
          >
            <MenuItem value="">Tümü</MenuItem>
            {PAGE_TYPES.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Değişiklik tipi"
            size="small"
            value={filters.changeType}
            onChange={(e) => setFilter('changeType', e.target.value)}
          >
            <MenuItem value="">Tümü</MenuItem>
            {CHANGE_TYPES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Sonuç"
            size="small"
            value={filters.result}
            onChange={(e) => setFilter('result', e.target.value)}
          >
            {RESULT_FILTERS.map((r) => (
              <MenuItem key={r.value || 'all'} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Etiket"
            size="small"
            value={filters.tag}
            onChange={(e) => setFilter('tag', e.target.value)}
            placeholder="checkout, cta..."
          />

          {hasActiveFilters && (
            <Button size="small" onClick={clearFilters}>
              Temizle
            </Button>
          )}
        </Box>
      </Paper>

      {loading ? (
        <DashboardListSkeleton rows={6} />
      ) : (
        <>
          {/* Tag pattern chips — click to filter by tag */}
          {data?.byTag?.length > 0 && (
            <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Popüler etiketler
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {data.byTag.slice(0, 16).map((item) => (
                  <Chip
                    key={item.key}
                    label={`${item.key} · ${item.total} test · %${item.winRate} kazanma`}
                    clickable
                    color={filters.tag === item.key ? 'primary' : 'default'}
                    variant={filters.tag === item.key ? 'filled' : 'outlined'}
                    onClick={() => setFilter('tag', filters.tag === item.key ? '' : item.key)}
                    size="small"
                  />
                ))}
              </Box>
            </Paper>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {data?.total ?? 0} rapor listeleniyor
          </Typography>

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rapor</TableCell>
                  <TableCell>Marka</TableCell>
                  <TableCell>Sayfa / Değişiklik</TableCell>
                  <TableCell>Etiketler</TableCell>
                  <TableCell>Sonuç</TableCell>
                  <TableCell align="right">İyileşme</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.reports || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Filtrelere uygun rapor bulunamadı
                    </TableCell>
                  </TableRow>
                ) : (
                  data.reports.map((report) => {
                    const outcome = getOutcomeChip(report.resultStatuses);
                    return (
                      <TableRow key={report.id} hover>
                        <TableCell>
                          <RouterLink
                            to={`/dashboard/reports/${report.id}`}
                            style={{ color: BRAND.purple, fontWeight: 600, textDecoration: 'none' }}
                          >
                            {report.name}
                          </RouterLink>
                          {report.hypothesis && (
                            <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
                              {report.hypothesis}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{report.brand}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{getPageTypeLabel(report.pageType)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getChangeTypeLabel(report.changeType)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(report.tags || []).map((t) => (
                              <Chip key={t} label={t} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={outcome.label} size="small" color={outcome.color} />
                        </TableCell>
                        <TableCell align="right">
                          {typeof report.maxImprovement === 'number'
                            ? `${report.maxImprovement > 0 ? '+' : ''}${report.maxImprovement.toFixed(1)}%`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default TestLibraryPage;
